import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { auth, db } from '../../config/firebase';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  updateProfile,
  sendPasswordResetEmail 
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { sendEmailNotification, sendWelcomeEmail } from '../../utils/emailService';

const AuthModal = () => {
  const {
    showAuthModal,
    setShowAuthModal,
    authMode,
    setAuthMode,
    setCurrentUser,
    setShowLanding,
    showNotification
  } = useAppContext();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  if (!showAuthModal) return null;

  const validateForm = () => {
    const newErrors = {};
    
    if (authMode === 'register') {
      if (!formData.name) {
        newErrors.name = 'Name is required';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.acceptTerms) {
        newErrors.acceptTerms = 'You must accept the terms and conditions';
      }
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!formData.email.includes('@')) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      let userCredential;
      let user;
      
      if (authMode === 'login') {
        // Handle Login
        userCredential = await signInWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );
        
        user = {
          id: userCredential.user.uid,
          name: userCredential.user.displayName || 'User',
          email: userCredential.user.email,
          registrationDate: userCredential.user.metadata.creationTime
        };
        
        // Success - close modal and set user
        setCurrentUser(user);
        setShowLanding(false);
        setShowAuthModal(false);
        showNotification('Welcome Back!', 'Successfully logged in', 'success');
        
      } else {
        // Handle Registration
        try {
          // Step 1: Create the user
          userCredential = await createUserWithEmailAndPassword(
            auth,
            formData.email,
            formData.password
          );
          
          // Step 2: Update profile
          try {
            await updateProfile(userCredential.user, {
              displayName: formData.name
            });
          } catch (profileError) {
            console.log('Profile update error (non-critical):', profileError);
          }
          
          // Step 3: Save to Firestore with registration date
          const registrationDate = new Date().toISOString();
          try {
            await setDoc(doc(db, 'users', userCredential.user.uid), {
              name: formData.name,
              email: formData.email,
              createdAt: registrationDate,
              registrationDate: registrationDate,
              abn: '33 689 275 581'
            });
          } catch (firestoreError) {
            console.log('Firestore save error (non-critical):', firestoreError);
          }
          
          // Step 4: Send admin notification email
          try {
            const timestamp = new Date().toLocaleString('en-AU', {
              timeZone: 'Australia/Sydney',
              dateStyle: 'medium',
              timeStyle: 'short'
            });
            
            const emailHtml = `
              <div style="font-family: Arial, sans-serif; padding: 20px; background-color: #f5f5f5;">
                <div style="max-width: 600px; margin: 0 auto; background-color: white; padding: 30px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
                  <h2 style="color: #333; border-bottom: 2px solid #4F46E5; padding-bottom: 10px;">New User Registration</h2>
                  
                  <div style="margin: 20px 0;">
                    <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Name:</strong> ${formData.name}</p>
                    <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Email:</strong> ${formData.email}</p>
                    <p style="color: #666; margin: 10px 0;"><strong style="color: #333;">Time:</strong> ${timestamp}</p>
                  </div>
                  
                  <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee;">
                    <p style="color: #999; font-size: 12px; margin: 0;">This is an automated notification from planAIdesign</p>
                    <p style="color: #999; font-size: 12px; margin: 5px 0;">View user in Firebase: <a href="https://console.firebase.google.com/project/planaidesign-prod/authentication/users" style="color: #4F46E5;">Authentication Console</a></p>
                  </div>
                </div>
              </div>
            `;
            
            await sendEmailNotification(
              `New User Registration - ${formData.name}`,
              emailHtml
            );
            
            console.log('Admin notification sent for new user registration');
          } catch (emailError) {
            console.log('Admin email notification error (non-critical):', emailError);
          }
          
          // Step 5: Send welcome email to new user
          try {
            const userData = {
              name: formData.name,
              email: formData.email,
              registrationDate: registrationDate
            };
            
            // Send welcome email
            await sendWelcomeEmail(userData);
            
            // Store user for follow-up emails tracking
            const registeredUsers = JSON.parse(localStorage.getItem('registered_users') || '[]');
            registeredUsers.push(userData);
            localStorage.setItem('registered_users', JSON.stringify(registeredUsers));
            
            console.log('Welcome email sent to new user');
          } catch (welcomeEmailError) {
            console.log('Welcome email error (non-critical):', welcomeEmailError);
          }
          
          // Step 6: Set user and close modal
          user = {
            id: userCredential.user.uid,
            name: formData.name,
            email: formData.email,
            registrationDate: registrationDate
          };
          
          // Clear form data
          setFormData({
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            acceptTerms: false
          });
          
          // Success - close modal and set user
          setCurrentUser(user);
          setShowLanding(false);
          setShowAuthModal(false);

          // Meta Pixel: registration completed
          if (window.fbq) window.fbq('track', 'CompleteRegistration');

          // Show success message AFTER closing modal
          setTimeout(() => {
            showNotification('Welcome!', 'Account created successfully. Check your email!', 'success');
          }, 100);
          
        } catch (error) {
          // Only show error if account creation actually failed
          if (error.code === 'auth/email-already-in-use') {
            setErrors({ email: 'This email is already registered. Please login instead.' });
          } else if (error.code === 'auth/weak-password') {
            setErrors({ password: 'Password should be at least 6 characters' });
          } else if (error.code === 'auth/invalid-email') {
            setErrors({ email: 'Invalid email address' });
          } else {
            setErrors({ email: 'Registration failed. Please try again.' });
          }
          console.error('Registration error:', error);
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      
      // Handle login errors
      if (error.code === 'auth/user-not-found') {
        setErrors({ email: 'No account found with this email' });
      } else if (error.code === 'auth/wrong-password') {
        setErrors({ password: 'Incorrect password' });
      } else if (error.code === 'auth/invalid-email') {
        setErrors({ email: 'Invalid email address' });
      } else {
        setErrors({ email: 'An error occurred. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePasswordReset = async () => {
    if (!formData.email) {
      setErrors({ email: 'Please enter your email address to reset your password' });
      return;
    }
    if (!formData.email.includes('@')) {
      setErrors({ email: 'Please enter a valid email address' });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await sendPasswordResetEmail(auth, formData.email);
      setResetSent(true);
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/user-not-found') {
        setErrors({ email: 'No account found with this email address' });
      } else if (error.code === 'auth/invalid-email') {
        setErrors({ email: 'Invalid email address' });
      } else {
        setErrors({ email: 'Failed to send reset email. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setAuthMode(authMode === 'login' ? 'register' : 'login');
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false
    });
    setErrors({});
    setResetMode(false);
    setResetSent(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-xl max-w-md w-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold">
            {resetMode ? 'Reset Password' : (authMode === 'login' ? 'Welcome Back' : 'Create Account')}
          </h3>
          <button
            onClick={() => { setShowAuthModal(false); setResetMode(false); setResetSent(false); }}
            className="text-2xl text-slate-400 hover:text-slate-600"
          >
            ×
          </button>
        </div>

        {/* Password Reset View */}
        {resetMode ? (
          resetSent ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 font-semibold mb-1">Reset email sent</p>
                <p className="text-green-700 text-sm">
                  We've sent a password reset link to <strong>{formData.email}</strong>. 
                  Please check your inbox and follow the link to reset your password.
                </p>
                <p className="text-green-600 text-xs mt-2">
                  If you don't see the email, check your spam folder.
                </p>
              </div>
              <button
                onClick={() => { setResetMode(false); setResetSent(false); }}
                className="w-full py-3 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              >
                Back to Login
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Enter the email address associated with your account and we'll send you a link to reset your password.
              </p>
              <div>
                <label className="block text-sm font-medium mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="john@example.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>
              <button
                onClick={handlePasswordReset}
                disabled={loading}
                className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                  loading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button
                onClick={() => { setResetMode(false); setErrors({}); }}
                className="w-full py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Back to Login
              </button>
            </div>
          )
        ) : (
          <>
        <form onSubmit={handleSubmit} className="space-y-4">
          {authMode === 'register' && (
            <div>
              <label className="block text-sm font-medium mb-2">Full Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="John Smith"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium mb-2">Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="john@example.com"
            />
            {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="••••••••"
            />
            {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
            {/* Forgot password link - hidden until SMTP configured
            {authMode === 'login' && (
              <button
                type="button"
                onClick={() => { setResetMode(true); setErrors({}); }}
                className="text-sm text-blue-600 hover:underline mt-1"
              >
                Forgot your password?
              </button>
            )}
            */}
          </div>
          
          {authMode === 'register' && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="••••••••"
                />
                {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleInputChange}
                  className="mr-2"
                />
                <label className="text-sm">
                  I accept the <span className="text-blue-600 cursor-pointer">Terms of Service</span> and <span className="text-blue-600 cursor-pointer">Privacy Policy</span>
                </label>
              </div>
              {errors.acceptTerms && <p className="text-red-500 text-sm">{errors.acceptTerms}</p>}
            </>
          )}
          
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-3 rounded-lg font-semibold transition-colors ${
              loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {loading ? 'Please wait...' : (authMode === 'login' ? 'Log In' : 'Create Account')}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            {authMode === 'login' ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={switchMode}
              className="text-blue-600 font-semibold hover:underline"
            >
              {authMode === 'login' ? 'Sign up' : 'Log in'}
            </button>
          </p>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;